// Single source of truth for the text a breakdown adjustment SENDS.
//
// Same discipline as `chat/render/answerMessage.ts`: the widget/buttons never
// mutate breakdown state themselves — every adjustment goes out as a normal,
// quotable user turn the agent reads and answers with a fresh
// `propose_breakdown` call (which supersedes the card + rotates the token).
// The `Breakdown:` prefix + ordinals keep the quote long and field-specific.
//
// GROUNDING PLANE — byte-exact English by contract (like `PRIORITY_OPTIONS`);
// never localize these.

/** Every self-labelled breakdown turn opens with this — the anchor the agent
 *  routes on (to `propose_breakdown`, never `propose_card`). Exported so a
 *  DISPLAY surface that shows these turns to the user (Chat, over the same
 *  session a breakdown adjustment was sent through) can strip it rather than
 *  hardcoding a second copy of a string it must match byte-for-byte to work. */
export const BREAKDOWN_MESSAGE_PREFIX = "Breakdown: ";

export const buildRemoveChildMessage = (ordinal: number): string =>
  `${BREAKDOWN_MESSAGE_PREFIX}remove sub-issue ${String(ordinal)}; fold its scope into the others.`;

export const buildMergeChildrenMessage = (
  fromOrdinal: number,
  intoOrdinal: number,
): string =>
  `${BREAKDOWN_MESSAGE_PREFIX}merge sub-issue ${String(fromOrdinal)} into sub-issue ${String(intoOrdinal)}.`;

export const buildRenameChildMessage = (
  ordinal: number,
  title: string,
): string => `${BREAKDOWN_MESSAGE_PREFIX}rename sub-issue ${String(ordinal)} to "${title}".`;

/** Free-text adjustment — still prefixed so the agent routes it to
 *  `propose_breakdown`, never to `propose_card` (which would churn the
 *  readiness token's slot hash mid-review). */
export const buildAdjustMessage = (instruction: string): string =>
  `${BREAKDOWN_MESSAGE_PREFIX}${instruction.trim()}`;

/** "Split this piece further" — the sub-issue is itself too big; ask the agent
 *  to break it into smaller pieces. Routes to propose_breakdown. */
export const buildSplitFurtherMessage = (ordinal: number): string =>
  `${BREAKDOWN_MESSAGE_PREFIX}split sub-issue ${String(ordinal)} into smaller pieces.`;

export const buildKeepSingleMessage = (issueIid: number): string =>
  `${BREAKDOWN_MESSAGE_PREFIX}keep issue #${String(issueIid)} as a single ticket.`;

/**
 * The "big but one acceptance criterion" case: ask the user for the distinct
 * PIECES the work ships as (not more acceptance criteria — that's the wrong
 * lens for splitting). NOT `Breakdown:`-prefixed on purpose: this routes to
 * re-refinement (propose_card turns the pieces into criteria), not to
 * propose_breakdown. Byte-stable English (grounding plane).
 */
export const buildPieceElicitationMessage = (): string =>
  "This is one large acceptance criterion. Ask me for the distinct pieces this " +
  "should ship as — a short name and a one-line “done when…” for " +
  "each — then refine the acceptance criteria into those pieces so it can be " +
  "split into sub-issues.";
